import { keywordHubs } from "./keyword-hubs";
import { episodes } from "./podcast-episodes";

export type SearchDoc = {
  /** Route path */
  to: string;
  /** Route params for dynamic routes */
  params?: Record<string, string>;
  title: string;
  desc: string;
  /** Grouping shown in the results list */
  kind: "Keyword page" | "Page" | "Podcast episode";
  /** Extra searchable terms (never rendered) */
  terms: string[];
  /** Higher wins ties */
  weight: number;
};

/** Core site pages — kept here so search, nav and sitemap share one vocabulary. */
const corePages: SearchDoc[] = [
  {
    to: "/",
    title: "Raj Shamani — Home",
    desc: "Age, net worth, biography, the Figuring Out podcast and Build Don't Talk, all in one place.",
    kind: "Page",
    terms: ["raj shamani", "home", "official website", "biography"],
    weight: 10,
  },
  {
    to: "/about",
    title: "About Raj Shamani",
    desc: "Full biography — age, education, family, net worth and career timeline.",
    kind: "Page",
    terms: ["about", "wiki", "biography", "education", "net worth", "age"],
    weight: 9,
  },
  {
    to: "/podcast",
    title: "Figuring Out Podcast",
    desc: "Every Figuring Out episode with summaries, transcripts and video.",
    kind: "Page",
    terms: ["podcast", "figuring out", "episodes", "youtube"],
    weight: 9,
  },
  {
    to: "/build-dont-talk",
    title: "Build, Don't Talk",
    desc: "Chapter-by-chapter summary, key ideas and where to buy the book.",
    kind: "Page",
    terms: ["book", "build dont talk", "summary", "buy"],
    weight: 8,
  },
  {
    to: "/ventures",
    title: "Ventures & Investments",
    desc: "House of X, Shamani Industries and the angel investment portfolio.",
    kind: "Page",
    terms: ["ventures", "house of x", "investments", "startups", "portfolio"],
    weight: 8,
  },
  {
    to: "/guest",
    title: "Be a Guest on Figuring Out",
    desc: "Pitch yourself for a Figuring Out episode — how selection works.",
    kind: "Page",
    terms: ["guest", "pitch", "apply", "be a guest"],
    weight: 6,
  },
  {
    to: "/faq",
    title: "Raj Shamani FAQ",
    desc: "Quick answers to the most-asked questions about Raj Shamani.",
    kind: "Page",
    terms: ["faq", "questions", "answers"],
    weight: 6,
  },
  {
    to: "/contact",
    title: "Contact Raj Shamani",
    desc: "Brand partnerships, speaking enquiries and the official email address.",
    kind: "Page",
    terms: ["contact", "email", "partnerships", "booking", "speaking"],
    weight: 7,
  },
  {
    to: "/guides/how-to-start-a-podcast-india",
    title: "How to Start a Podcast in India",
    desc: "A practical guide to launching, recording and growing a podcast in India.",
    kind: "Page",
    terms: ["guide", "podcast", "start", "india", "how to"],
    weight: 5,
  },
  {
    to: "/guides/d2c-brands-india",
    title: "D2C Brands in India",
    desc: "How Indian direct-to-consumer brands are built, funded and scaled.",
    kind: "Page",
    terms: ["d2c", "brands", "india", "guide", "ecommerce"],
    weight: 5,
  },
  {
    to: "/guides/how-to-become-an-ai-engineer-india",
    title: "How to Become an AI Engineer in India",
    desc: "A 2026 roadmap: skills, projects, salaries and where to start without a CS degree.",
    kind: "Page",
    terms: ["ai", "ai engineer", "career", "india", "guide", "prompt engineering", "jobs"],
    weight: 5,
  },
  {
    to: "/guides/how-to-use-ai-in-business-india",
    title: "How to Use AI in Your Business in India",
    desc: "Which workflows to automate first, what to avoid, and how to measure the return.",
    kind: "Page",
    terms: ["ai", "business", "automation", "india", "guide", "founders", "tools"],
    weight: 5,
  },
  {
    to: "/guides/how-to-become-an-angel-investor-india",
    title: "How to Become an Angel Investor in India",
    desc: "Cheque sizes, deal flow, diligence, paperwork and tax — a practical starting guide.",
    kind: "Page",
    terms: ["angel investor", "investing", "startups", "india", "guide", "cheque size", "deal flow"],
    weight: 5,
  },
  {
    to: "/guides/how-to-grow-on-instagram-india",
    title: "How to Grow on Instagram in India",
    desc: "Niche, Reels formats, hooks, posting cadence and how Indian creators actually earn.",
    kind: "Page",
    terms: ["instagram", "reels", "creator", "grow", "followers", "india", "guide", "brand deals"],
    weight: 5,
  },

];


/** The full, SSR-safe search corpus — built from the shared keyword index. */
export const searchDocs: SearchDoc[] = [
  ...keywordHubs.map<SearchDoc>((h) => ({
    to: h.to,
    title: h.title,
    desc: h.desc,
    kind: "Keyword page",
    terms: [h.inlineLabel, ...h.tags, h.to.replace(/[-/]/g, " ").trim()],
    weight: 10 * Number(h.priority),
  })),
  ...corePages,
  ...episodes.map<SearchDoc>((e) => ({
    to: "/podcast/$slug",
    params: { slug: e.slug },
    title: `${e.g} — ${e.t}`,
    desc: e.summary.slice(0, 160).trim() + "…",
    kind: "Podcast episode",
    terms: [e.g, e.t, e.n, "figuring out", "episode", "podcast", ...e.takeaways],
    weight: 4,
  })),
];

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();

export type SearchResult = SearchDoc & { score: number };

/**
 * Pure, dependency-free scoring so results can be computed identically on the
 * server (SSR) and on the client.
 */
export function searchSite(rawQuery: string, limit = 25): SearchResult[] {
  const q = norm(rawQuery);
  if (!q) return [];
  const words = q.split(" ").filter((w) => w.length > 1);
  if (words.length === 0) return [];

  const results: SearchResult[] = [];

  for (const doc of searchDocs) {
    const title = norm(doc.title);
    const haystack = norm([doc.title, doc.desc, doc.terms.join(" ")].join(" "));

    let score = 0;
    if (title.includes(q)) score += 60;
    if (haystack.includes(q)) score += 30;

    for (const w of words) {
      if (title.includes(w)) score += 12;
      else if (haystack.includes(w)) score += 5;
    }

    // Every word must appear somewhere, otherwise it isn't a match.
    const allWordsPresent = words.every((w) => haystack.includes(w));
    if (!allWordsPresent && !haystack.includes(q)) continue;
    if (score === 0) continue;

    results.push({ ...doc, score: score + doc.weight });
  }

  return results
    .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title))
    .slice(0, limit);
}

/** Suggested queries shown on the empty search page. */
export const popularSearches = [
  "raj shamani age",
  "raj shamani net worth",
  "figuring out podcast",
  "build dont talk",
  "shamani industries",
  "raj shamani family",
];
